import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useCallback, useSyncExternalStore } from 'react';

import type { Permission } from '@/constants/permissions';
import { QUERY_KEYS } from '@/constants/query-keys';
import { sessionTokenStore } from '@/services/session/token-store';

import { getMe } from '../api/auth.api';
import type { AuthProfile } from '../schemas/auth.schema';

const PROFILE_STALE_MS = 60_000;

/** The bearer token, or null when signed out. Re-renders on sign-in and sign-out (any tab). */
export function useSessionToken(): string | null {
  return useSyncExternalStore(sessionTokenStore.subscribe, sessionTokenStore.get);
}

/**
 * The signed-in user's profile and permissions. Refetched when the window regains focus, so a
 * role change made by an admin reaches an open screen without signing out.
 */
export function useCurrentUser(): UseQueryResult<AuthProfile> {
  const token = useSessionToken();
  return useQuery({
    queryKey: QUERY_KEYS.auth.me(),
    queryFn: ({ signal }) => getMe(signal),
    enabled: token !== null,
    staleTime: PROFILE_STALE_MS,
    refetchOnWindowFocus: true,
  });
}

/** The signed-in user. Only for components rendered inside <RequireAuth>, which loads it. */
export function useAuthUser(): AuthProfile {
  const { data } = useCurrentUser();
  if (!data) throw new Error('useAuthUser must be used inside <RequireAuth>');
  return data;
}

/**
 * `can('members:write')` — whether the signed-in user holds a permission. Show or hide UI with
 * it; never check a role name. The API enforces permissions regardless.
 */
export function usePermissions(): { can: (permission: Permission) => boolean } {
  const { data } = useCurrentUser();
  const permissions = data?.permissions;
  const can = useCallback(
    (permission: Permission) => permissions?.includes(permission) ?? false,
    [permissions],
  );
  return { can };
}
